import { FadeIn, MaskedLine, Chapter } from "@/components/shared/Reveal";
import Marquee from "@/components/shared/Marquee";
import CTASection from "@/components/shared/CTASection";
import { RefreshCw, Hammer, Wrench, ShoppingBag, Plug, Archive } from "lucide-react";

const paths = [
  {
    icon: RefreshCw,
    title: "Re-platform",
    body: "Move the application to modern infrastructure — cloud, containers, managed services — with minimal code change. Fastest path to operational relief.",
  },
  {
    icon: Hammer,
    title: "Rebuild",
    body: "Re-implement the application on a modern stack when the current one constrains the business more than it serves it. Business logic leads; technology follows.",
  },
  {
    icon: Wrench,
    title: "Refactor",
    body: "Restructure the code you have — modularize, decouple, modernize incrementally — while the system keeps running and the business keeps moving.",
  },
  {
    icon: ShoppingBag,
    title: "Replace",
    body: "Adopt a commercial platform or SaaS when the capability is a commodity. Build only what differentiates you; buy what does not.",
  },
  {
    icon: Plug,
    title: "Integrate",
    body: "Sometimes the legacy system stays exactly where it is. APIs, events, and data flows connect it to modern experiences without risky surgery.",
  },
  {
    icon: Archive,
    title: "Retire",
    body: "The most underrated modernization: decommission what no longer earns its keep, preserving data and history while shedding cost and risk.",
  },
];

const Modernization = () => (
  <main data-testid="modernization-page" className="bg-ink">
    <section className="relative pt-40 pb-24 md:pb-32 overflow-hidden">
      <div className="absolute -top-32 right-0 w-[560px] h-[560px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <MaskedLine delay={0.1}>
          <p className="label-mono">Legacy Application Modernization</p>
        </MaskedLine>
        <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-100">
          <MaskedLine delay={0.25}>Evolution,</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">Not Revolution.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-slate-400 font-light leading-relaxed">
            Modernization is not a product we sell or a platform we favor. It is a spectrum of
            options — and the discipline of choosing the right one for each application, with
            your people at the center of the decision.
          </p>
        </MaskedLine>
      </div>
    </section>

    <Marquee />

    <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36 space-y-24 md:space-y-32">
      <Chapter number="01" kicker="Where it begins" title="Assessment before prescription">
        Every engagement starts with understanding, not a proposal. We inventory your
        applications, map their business value and technical health, talk to the people who
        depend on them, and surface the business logic buried in decades of code. Only then do
        options get evaluated — against your constraints, not our preferences.
      </Chapter>
      <Chapter number="02" kicker="The crown jewels" title="Business logic preservation">
        The rules inside your legacy systems — pricing, eligibility, workflow, exception
        handling — represent decades of refined business knowledge. We treat that logic as an
        asset to be extracted, documented, and carried forward, never as debris to be bulldozed.
        AI-assisted analysis helps us surface what the documentation forgot.
      </Chapter>
    </section>

    <section className="border-t hairline bg-ink-light">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36">
        <FadeIn>
          <p className="label-mono mb-4">03 // The Spectrum</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 max-w-2xl leading-tight">
            Six paths. No predetermined destination.
          </h2>
          <p className="mt-6 max-w-2xl text-slate-400 font-light leading-relaxed text-lg">
            We have no technology to sell you. Each application earns its own answer.
          </p>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {paths.map((p, i) => (
            <FadeIn
              key={p.title}
              delay={0.08 * i}
              className="group border hairline bg-ink p-8 md:p-10 transition-[border-color,transform,background-color] duration-500 hover:border-amber-600/50 hover:-translate-y-1 hover:bg-slate-900/60"
            >
              <p.icon className="text-gold mb-6" size={26} strokeWidth={1.5} />
              <p className="font-mono-tech text-amber-500/60 text-xs tracking-[0.3em] mb-3">
                PATH 0{i + 1}
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-slate-100 mb-4">{p.title}</h3>
              <p className="text-slate-400 font-light leading-relaxed text-sm">{p.body}</p>
            </FadeIn>
          ))}
        </div>
        <FadeIn className="mt-16 border-l-2 border-amber-500/70 pl-8 max-w-3xl">
          <p className="font-serif text-2xl md:text-3xl text-amber-100/90 italic leading-snug">
            "We arrive with questions, not a quotation. The technology recommendation comes
            last — after the people, the logic, and the canvas are understood."
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="border-t hairline bg-ink">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeIn className="lg:col-span-5">
          <p className="label-mono mb-4">04 // Accelerated by AI</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 leading-tight">
            See the future before you fund it.
          </h2>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <p className="text-slate-400 font-light leading-relaxed text-lg">
            AI-assisted development changes the economics of modernization. We prototype the
            modern version of a legacy workflow in days, put it in front of the people who use
            it, and let evidence — not enthusiasm — drive the decision to rebuild, refactor, or
            integrate.
          </p>
          <p className="mt-6 text-slate-400 font-light leading-relaxed text-lg">
            AI Agents and Build Agent compress the distance from assessment to working software.
            You validate the destination before committing to the journey.
          </p>
        </FadeIn>
      </div>
    </section>

    <CTASection
      title="Start with an honest assessment."
      sub="A candid look at your legacy estate — the options, the trade-offs, and the path we would take if it were our system."
    />
  </main>
);

export default Modernization;
